import * as React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AlertOctagon, Clock, MessageSquare, CheckSquare } from "lucide-react";

/**
 * CardChip — the compact card representation in a list column. Sortable
 * (drag source) unless `dragging` prop is passed (used inside DragOverlay).
 */
function daysUntil(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const now = new Date();
  const diff = Math.floor((d - new Date(now.toISOString().slice(0, 10) + "T00:00:00Z")) / (24 * 3600 * 1000));
  return diff;
}

export default function CardChip({ card, labels, blocked = false, dragging = false, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `card:${card.id}`,
    disabled: dragging,
  });
  const style = dragging ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const cardLabels = (card.label_ids || [])
    .map((id) => labels.find((l) => l.id === id))
    .filter(Boolean);

  const checklist = card.checklist || [];
  const checklistDone = checklist.filter((i) => i.done).length;

  const due = card.due_date ? daysUntil(card.due_date) : null;
  let dueTone = "";
  if (due !== null) {
    if (due < 0) dueTone = "chip-coral";
    else if (due <= 2) dueTone = "chip-amber";
    else dueTone = "";
  }

  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(dragging ? {} : listeners)}
      onClick={(e) => { if (!isDragging) onClick && onClick(e); }}
      className={`w-full text-left rounded-md bg-card border border-brand-rule shadow-soft hover:shadow-card transition-shadow px-2.5 py-2 ${
        dragging ? "cursor-grabbing rotate-1" : "cursor-grab"
      }`}
      data-testid={`card-chip-${card.id}`}
      data-blocked={blocked ? "true" : undefined}
    >
      {/* Labels row */}
      {cardLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {cardLabels.map((l) => (
            <span
              key={l.id}
              className="chip text-[9.5px] font-semibold"
              style={{ background: `${l.color}22`, color: l.color, borderColor: `${l.color}44` }}
              data-testid={`card-${card.id}-label-${l.id}`}
            >
              {l.name}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-start gap-1.5">
        {blocked && (
          <AlertOctagon size={12} className="text-brand-coral mt-0.5 shrink-0"
                        data-testid={`card-${card.id}-blocked`} />
        )}
        <div className="flex-1 min-w-0 text-[12.5px] font-medium text-brand-ink break-words">
          {card.title}
        </div>
      </div>
      {/* Meta row */}
      <div className="mt-1.5 flex items-center gap-3 text-[10.5px] text-brand-muted">
        {due !== null && (
          <span className={`inline-flex items-center gap-1 mono-num chip ${dueTone}`} data-testid={`card-${card.id}-due`}>
            <Clock size={9.5} /> {card.due_date}
          </span>
        )}
        {checklist.length > 0 && (
          <span className="inline-flex items-center gap-1 mono-num"
                data-testid={`card-${card.id}-checklist`}>
            <CheckSquare size={10} /> {checklistDone}/{checklist.length}
          </span>
        )}
        {(card.assignee_ids || []).length > 0 && (
          <div className="ml-auto flex -space-x-1.5">
            {(card.assignee_ids || []).slice(0, 3).map((uid) => (
              <span key={uid}
                    className="h-5 w-5 rounded-full bg-brand-violet text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                {uid.slice(0, 1).toUpperCase()}
              </span>
            ))}
            {(card.assignee_ids || []).length > 3 && (
              <span className="h-5 w-5 rounded-full bg-brand-hush text-brand-muted text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                +{(card.assignee_ids || []).length - 3}
              </span>
            )}
          </div>
        )}
      </div>
      {card.logged_minutes ? (
        <div className="mt-1 flex items-center gap-1 text-[10px] mono-num text-brand-muted">
          logged {Math.floor(card.logged_minutes / 60)}h {card.logged_minutes % 60}m
        </div>
      ) : null}
    </button>
  );
}
